import  { useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, FileText, ExternalLink, Trash2, Upload } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { voucherService } from '../../../../../../services/vouchers/voucherService';
import CustomInput from '../../../../../../components/forms/CustomInput';
import { getAssetUrl } from '../../utils/dashboardHelpers';

const ImportantDocumentsCard = ({ voucher, id, setVoucher }) => {
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [docName, setDocName] = useState('');
  const [docFile, setDocFile] = useState(null);
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState(null);

  const importantDocs = voucher?.important_documents || [];

  const handleAddDocument = async (e) => {
    e?.preventDefault();
    if (!docName.trim()) {
      toast.error('Please enter a document name.');
      return;
    }
    if (!docFile) {
      toast.error('Please select a file to upload.');
      return;
    }

    setIsUploadingDoc(true);
    const toastId = toast.loading('Uploading and attaching document...');
    try {
      const formData = new FormData();
      formData.append('name', docName.trim());
      formData.append('file', docFile);

      const targetId = voucher?._id || id;
      const res = await voucherService.uploadImportantDocument(targetId, formData);

      toast.dismiss(toastId);
      toast.success('Document added successfully!');

      const updatedDocs = res?.important_documents || [
        ...(voucher?.important_documents || []),
        res?.document || { name: docName.trim(), filename: docFile.name, uploaded_at: new Date() }
      ];

      setVoucher(prev => ({ ...prev, important_documents: updatedDocs }));
      setIsDocModalOpen(false);
      setDocName('');
      setDocFile(null);
    } catch (err) {
      toast.dismiss(toastId);
      console.error('Failed to add document:', err);
      toast.error(err.response?.data?.message || 'Failed to upload document.');
    } finally {
      setIsUploadingDoc(false);
    }
  };

  const handleDeleteDocument = async (docId, docName = 'document') => {
    if (!window.confirm(`Are you sure you want to remove "${docName}"?`)) return;

    setDeletingDocId(docId);
    const toastId = toast.loading(`Removing ${docName}...`);
    try {
      const targetId = voucher?._id || id;
      const res = await voucherService.deleteImportantDocument(targetId, docId);

      toast.dismiss(toastId);
      toast.success('Document removed successfully!');

      const updatedDocs =
        res?.important_documents ||
        (voucher?.important_documents || []).filter(d => (d._id || d.id) !== docId);
      setVoucher(prev => ({ ...prev, important_documents: updatedDocs }));
    } catch (err) {
      toast.dismiss(toastId);
      console.error('Failed to delete document:', err);
      toast.error('Failed to remove document.');
    } finally {
      setDeletingDocId(null);
    }
  };

  return (
    <>
      <div className="vc-card mt-4">
        <div className="vc-card-head flex items-center justify-between gap-2 flex-wrap">
          <h3 className="flex items-center gap-2 text-sm font-medium text-slate-600 tracking-tight">
            <span className="w-7 h-7 rounded-lg bg-teal-50 border border-teal-100 flex items-center justify-center shrink-0">
              <FileText size={15} className="text-[#067978]" />
            </span>
            <span>Important Documents</span>
          </h3>
          <button
            onClick={() => {
              setIsDocModalOpen(true);
              setDocName('');
              setDocFile(null);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#067978] hover:bg-[#056362] text-white rounded-xl text-xs font-medium transition-all shadow-2xs active:scale-95 cursor-pointer"
            type="button"
          >
            <Plus size={14} /> Add Document
          </button>
        </div>

        {importantDocs.length === 0 ? (
          <div className="text-sm text-[#8a9aa5] py-4 px-3 flex items-center gap-2.5 bg-slate-50/70 rounded-xl border border-dashed border-slate-200 mt-2">
            <FileText size={18} className="text-slate-400 shrink-0" />
            <span className="text-xs font-medium text-slate-500">
              No important documents uploaded for this voucher yet.
            </span>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-2">
            {importantDocs.map((doc, idx) => (
              <div
                key={doc._id || idx}
                className="flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100/80 border border-slate-200/80 rounded-xl transition-all group relative"
              >
                <div className="flex items-center gap-2.5 min-w-0 pr-2">
                  <div className="w-8 h-8 rounded-lg bg-teal-50 text-[#067978] flex items-center justify-center shrink-0 border border-teal-100/80">
                    <FileText size={16} />
                  </div>
                  <div className="min-w-0">
                    <p
                      className="text-xs font-medium text-slate-700 truncate"
                      title={doc.name || doc.filename || `Document #${idx + 1}`}
                    >
                      {doc.name || doc.filename || `Document #${idx + 1}`}
                    </p>
                    <p className="text-[10px] text-slate-400 truncate">
                      {doc.assignment_label || (doc.day_number && doc.event_title ? `Day ${doc.day_number} · ${doc.event_title}` : null) || (doc.filename
                        ? doc.filename
                        : doc.uploaded_at
                        ? new Date(doc.uploaded_at).toLocaleDateString()
                        : 'Important Document')}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  {doc.url && (
                    <a
                      href={getAssetUrl(doc.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-white hover:bg-[#067978] text-[#067978] hover:text-white border border-slate-200 hover:border-[#067978] rounded-lg text-xs font-bold transition-all shadow-2xs"
                      title="View Document"
                    >
                      <ExternalLink size={12} />
                      <span className="hidden sm:inline">View</span>
                    </a>
                  )}
                  <button
                    onClick={() =>
                      handleDeleteDocument(doc._id || idx, doc.name || doc.filename || 'document')
                    }
                    disabled={deletingDocId === (doc._id || idx)}
                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100 cursor-pointer"
                    title="Delete Document"
                    type="button"
                  >
                    {deletingDocId === (doc._id || idx) ? (
                      <div className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Trash2 size={13} />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Document Modal */}
      {typeof document !== 'undefined' && createPortal(
        <div
          className={`vc-overlay ${isDocModalOpen ? 'vc-open' : ''}`}
          onClick={(e) => {
            if (e.target.classList.contains('vc-overlay')) setIsDocModalOpen(false);
          }}
        >
          <div className="vc-modal !max-w-md" role="dialog" aria-modal="true" aria-label="Add Important Document">
            <div className="vc-m-head">
              <h4 className="flex items-center gap-2">
                <FileText size={16} className="text-[#067978]" />
                <span>Add Important Document</span>
              </h4>
              <button className="vc-m-close" onClick={() => setIsDocModalOpen(false)}>✕</button>
            </div>
            <form onSubmit={handleAddDocument} className="vc-m-body space-y-4 text-left">
              <div>
                <CustomInput
                  label="Document Name *"
                  value={docName}
                  onChange={(e) => setDocName(e.target.value)}
                  placeholder="e.g. Ferry Tickets, Scuba Waiver, Travel Insurance"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 tracking-wide mb-1.5">
                  Attach File (PDF, Image) <span className="text-rose-500 ml-0.5">*</span>
                </label>
                <div className="border-2 border-dashed border-slate-200 hover:border-[#067978]/60 rounded-2xl p-4 text-center bg-slate-50/50 hover:bg-teal-50/30 transition-all">
                  <input
                    type="file"
                    id="docFileInput"
                    className="hidden"
                    accept=".pdf,image/png,image/jpeg,image/jpg"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setDocFile(e.target.files[0]);
                      }
                    }}
                  />
                  <label htmlFor="docFileInput" className="cursor-pointer flex flex-col items-center justify-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-teal-50 text-[#067978] flex items-center justify-center border border-teal-100">
                      <Upload size={18} />
                    </div>
                    {docFile ? (
                      <div className="text-xs font-bold text-slate-800 break-all">
                        {docFile.name} ({(docFile.size / 1024 / 1024).toFixed(2)} MB)
                      </div>
                    ) : (
                      <>
                        <span className="text-xs font-bold text-slate-700">Click to browse file</span>
                        <span className="text-[10px] text-slate-400">PDF, JPG, PNG up to 10MB</span>
                      </>
                    )}
                  </label>
                </div>
              </div>

              <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsDocModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUploadingDoc}
                  className="px-5 py-2 bg-[#067978] hover:bg-[#056362] text-white rounded-xl text-xs font-bold transition-all shadow-sm active:scale-95 disabled:opacity-50 cursor-pointer flex items-center gap-1.5"
                >
                  {isUploadingDoc ? 'Uploading...' : 'Save & Attach'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ImportantDocumentsCard;
